import { hubspotRecordUrl, hubspotTaskUrl, type HubSpotRecordType } from '@/lib/hubspotUrl'
import type { DashboardSnapshot, TodayItem } from '../types'
import type { ZephChatSeed } from '../hooks/useStartZephChat'

type HubSpotConn = DashboardSnapshot['hubspot']

// Shared briefing preamble — "medium chatty": lead with the concrete details, then
// one clear recommendation, kept tight. Unchanged from the shipped task/ticket
// seeds so every row in the list opens a conversation with the same voice.
const BRIEF_STYLE =
  `Open the conversation yourself — you already have the record, so don't ask which one. ` +
  `Lead with the concrete details, then give one clear recommended next step. ` +
  `Keep it conversational but tight — a few short sentences, not a wall of text.`

// Only these object types have a HubSpot record page; a task is anchored on an
// associated contact instead (see hubspotTaskUrl), and an approval is a Zeph-side
// concept with no CRM record at all.
const RECORD_TYPES: Record<string, HubSpotRecordType | undefined> = {
  contact: 'contact',
  company: 'company',
  deal: 'deal',
  ticket: 'ticket',
}

export function todayItemHubSpotUrl(item: TodayItem, hs: HubSpotConn): string | null {
  if (!item.hubspot) return null
  const { objectType, objectId, associatedContactId } = item.hubspot
  if (objectType === 'task') return hubspotTaskUrl(hs.portalId, objectId, associatedContactId, hs.uiDomain)
  const recordType = RECORD_TYPES[objectType]
  return recordType ? hubspotRecordUrl(hs.portalId, recordType, objectId, hs.uiDomain) : null
}

function formatDate(iso: string | null): string {
  if (!iso) return 'not set'
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' })
  } catch {
    return 'not set'
  }
}

/**
 * Builds the Zeph conversation seed for a Today row. `seedKey` is one conversation
 * per source record, so re-clicking a row reopens its existing chat (see ChatPage);
 * `briefing` is posted hidden and Zeph opens the conversation itself.
 */
export function todayChatSeed(item: TodayItem, hs: HubSpotConn): ZephChatSeed {
  const hubspotUrl = todayItemHubSpotUrl(item, hs)

  switch (item.kind) {
    case 'approval':
      return {
        seedKey: `approval:${item.detail.conversationId}`,
        title: item.title,
        briefing: [
          `[[zeph:brief]] The user opened this from the approval waiting on them on their dashboard. ` +
            `Remind them what you are asking to do and why, then ask for a decision. ${BRIEF_STYLE}`,
          ``,
          `Waiting on: ${item.detail.tools.map((t) => t.summary).join('; ')}`,
        ].join('\n'),
      }

    case 'task':
      return {
        seedKey: `hubspot-task:${item.detail.taskId}`,
        title: `Task: ${item.title}`,
        briefing: [
          `[[zeph:brief]] The user just opened this chat from their dashboard by clicking a HubSpot task assigned to them. ` +
            BRIEF_STYLE,
          ``,
          `Task: "${item.title}"`,
          `Task ID: ${item.detail.taskId}`,
          item.detail.dueAt
            ? `Due: ${formatDate(item.detail.dueAt)}${item.detail.overdue ? ' — OVERDUE' : ''}`
            : 'Due: not set',
          item.detail.contacts.length > 0
            ? `Associated contact(s): ${item.detail.contacts.map((c) => c.name).join(', ')}`
            : 'Associated contacts: none',
        ].join('\n'),
        hubspotUrl,
      }

    case 'ticket':
      return {
        seedKey: `hubspot-ticket:${item.detail.ticketId}`,
        title: `Ticket: ${item.title}`,
        briefing: [
          `[[zeph:brief]] The user just opened this chat from their dashboard by clicking an open HubSpot ticket. ` +
            BRIEF_STYLE,
          ``,
          `Ticket: "${item.title}"`,
          `Ticket ID: ${item.detail.ticketId}`,
          `Pipeline / stage: ${item.detail.pipelineLabel} · ${item.detail.stageLabel}`,
          item.detail.priority ? `Priority: ${item.detail.priority}` : 'Priority: not set',
        ].join('\n'),
        hubspotUrl,
      }

    case 'lead':
      return {
        seedKey: `qualification:${item.detail.runId}`,
        title: `Lead: ${item.detail.contactName ?? item.detail.company ?? `Contact ${item.detail.contactId}`}`,
        briefing: [
          `[[zeph:brief]] The user just opened this chat from their dashboard by clicking a lead you qualified. ` +
            `Recap what you found and recommend how to approach them. ${BRIEF_STYLE}`,
          ``,
          `Contact: ${item.detail.contactName ?? 'name unknown'}`,
          `Contact ID: ${item.detail.contactId}`,
          item.detail.company ? `Company: ${item.detail.company}` : 'Company: unknown',
          `Your score: ${item.detail.score} (${item.detail.verdict})`,
          `Your reasoning: ${item.detail.reasoning}`,
        ].join('\n'),
        hubspotUrl,
      }

    case 'deal':
      return {
        seedKey: `hubspot-deal:${item.detail.dealId}`,
        title: `Deal: ${item.detail.dealName ?? item.detail.dealId}`,
        briefing: [
          `[[zeph:brief]] The user just opened this chat from their dashboard by clicking a deal that has gone quiet. ` +
            `Say what you know about it and recommend how to revive it. ${BRIEF_STYLE}`,
          ``,
          `Deal: "${item.detail.dealName ?? `Deal ${item.detail.dealId}`}"`,
          `Deal ID: ${item.detail.dealId}`,
          `Pipeline / stage: ${item.detail.pipelineLabel} · ${item.detail.stageLabel}`,
          item.detail.amount !== null ? `Amount: ${item.detail.amount}` : 'Amount: not set',
          `No activity for ${item.detail.daysSinceLastModified} days`,
        ].join('\n'),
        hubspotUrl,
      }
  }
}

/**
 * Formats a deal amount with the portal's own currency when HubSpot gave us one,
 * and as a plain grouped number when it did not — a guessed symbol on a money
 * figure is worse than no symbol.
 */
export function formatAmount(amount: number | null, currencyCode: string | null): string | null {
  if (amount === null) return null
  try {
    if (currencyCode) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 0,
      }).format(amount)
    }
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(amount)
  } catch {
    return String(amount)
  }
}
